import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, OSKIcon, OSKIconType, OSKThemeType, useClickAway } from 'oskcomponents';
import styled, { useTheme } from 'styled-components';
import { GlobalZIndex } from '~/constants';

type FilterPanelButtonProps = {
    /** The icon code to use on the left hand side of the button */
    icon?: OSKIconType;
    /** The content of the button, placed to the right of the icon */
    children: React.ReactNode | Array<React.ReactNode>;
    /** The panel to show / position when the button is clicked. */
    panel?: React.ReactNode;
    /** Pass-through classname from styled-components */
    className?: string;
    /** The condition under which the clear function is shown */
    showClear?: boolean;
    /** Whether to display a pop-out button at all. */
    showExpand?: boolean;
    /** Whether to display a border */
    showBorder?: boolean;
    /** Whether the filter is visible in the interface */
    visible?: boolean;
    /** Optional background color override */
    bgColor?: string;
    /** The function that's called when the button is clicked */
    onClick?: () => void;
    /** The function that's called when the clear button is clicked */
    clearFilter?: () => void;
};

export type FilterPanelButtonHandle = {
    /** Force-close the panel for this filter */
    closePanel: () => void;
};

const FilterPanelButtonBase = React.forwardRef<FilterPanelButtonHandle, FilterPanelButtonProps>(
    (
        {
            icon,
            children,
            panel,
            className,
            showClear,
            showExpand = true,
            showBorder = true,
            visible = true,
            bgColor,
            onClick,
            clearFilter,
            ...props
        }: FilterPanelButtonProps,
        ref,
    ) => {
        const theme = useTheme() as OSKThemeType;
        const buttonRef = useRef<HTMLButtonElement & HTMLInputElement>(null);
        const panelRef = useRef(null);

        const [showPanel, setShowPanel] = useState<boolean>(false);
        const [borderVisible, setBorderVisible] = useState<boolean>(showBorder);
        const [highlightBorder, setHighlightBorder] = useState<boolean>(false);

        useClickAway(
            panelRef,
            () => {
                setShowPanel(false);
            },
            showPanel,
        );

        useImperativeHandle(ref, () => {
            return {
                closePanel() {
                    setShowPanel(false);
                },
            };
        });

        useEffect(() => {
            setBorderVisible(showBorder);
        }, [showBorder]);

        const borderColor = borderVisible
            ? showPanel || highlightBorder
                ? theme.colors.accent
                : theme.colors.black200
            : 'transparent';

        return (
            <Box className={className} style={{ display: visible ? 'inherit' : 'none' }}>
                <Box className="filter-panel-container">
                    <Box
                        className="filter-panel-button"
                        style={{
                            border: '1px solid',
                            borderColor: borderColor,
                            borderWidth: '1px 0px 1px 1px',
                        }}
                        cursor="pointer"
                        ref={buttonRef}
                        onClick={() => {
                            onClick && onClick();

                            if (panel) {
                                setShowPanel(!showPanel);
                            }
                        }}
                        {...props}
                    >
                        <Box row center="all" cursor="pointer">
                            {icon && <OSKIcon code={icon} fill="black" style={{ marginRight: '16px' }} scale={120} />}
                            {children}
                        </Box>
                    </Box>

                    {showExpand || showClear ? (
                        <Box
                            className="filter-panel-function"
                            style={{
                                border: '1px solid',
                                borderColor: borderColor,
                                borderWidth: '1px 1px 1px 0px',
                            }}
                            onClick={() => {
                                if (showClear) {
                                    clearFilter && clearFilter();
                                    setHighlightBorder(true);
                                    setInterval(() => setHighlightBorder(false), 500);
                                } else {
                                    setShowPanel(!showPanel);
                                }
                            }}
                            center="all"
                        >
                            {showClear ? (
                                <OSKIcon code="times" style={{ margin: '0px 8px' }} />
                            ) : showPanel ? (
                                <OSKIcon code="arrow-up" style={{ margin: '0px 8px' }} />
                            ) : (
                                <OSKIcon code="arrow-down" style={{ margin: '0px 8px' }} />
                            )}
                        </Box>
                    ) : (
                        <Box
                            className="filter-panel-function"
                            style={{
                                border: '1px solid',
                                borderColor: borderColor,
                                borderWidth: '1px 1px 1px 0px',

                                marginRight: '8px',
                                height: '100%',
                                width: '10px',
                            }}
                        >
                            &nbsp; &nbsp;
                        </Box>
                    )}
                </Box>

                {showPanel && (
                    <Box
                        ref={panelRef}
                        style={{
                            position: 'absolute',
                            top: `${(buttonRef.current?.offsetTop ?? 0) + (buttonRef.current?.offsetHeight ?? 0)}px`,
                            left: `${buttonRef.current?.offsetLeft}px`,
                            zIndex: GlobalZIndex.AOITools,
                        }}
                    >
                        {panel}
                    </Box>
                )}
            </Box>
        );
    },
);

const FilterPanelButton = styled(FilterPanelButtonBase)`
    .filter-panel-container {
        height: 36px;
        margin-bottom: 8px;
    }
    .filter-panel-button {
        background-color: ${(props: any) => props.bgColor ?? 'white'};
        border-radius: 7px 0px 0px 7px;
        margin-left: 8px;
        padding: 0px 8px 0px 18px;
        color: black;
        cursor: pointer !important;
        transition: all 0.25s;
        user-select: none;
    }

    .filter-panel-function {
        background-color: ${(props: any) => props.bgColor ?? 'white'};
        border-radius: 0px 7px 7px 0px;
        color: black;
        cursor: pointer !important;
        transition: all 0.25s;
    }

    .filter-panel-function:hover {
        ${(props: any) => {
            if (props.showClear !== false || props.showExpand !== false)
                return `background-color: ${props.theme.colors.gray50} !important;`;
            return '';
        }}
    }

    .filter-panel-container:hover {
        .filter-panel-button {
            ${(props: any) => (props.bgColor ? `opacity: .6;` : `background-color: ${props.theme.colors.gray25};`)}
        }
        .filter-panel-function {
            ${(props: any) => (props.bgColor ? `opacity: .6;` : `background-color: ${props.theme.colors.gray25};`)}
        }
    }
`;

export { FilterPanelButton };
export type { FilterPanelButtonProps };
